// src/components/App/GoogleAuthConfirmHandler.jsx

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Button, Alert, Stack } from '@mui/material';
import SpinningCustomLogo from '../Common/SpinningCustomLogo';
import { confirmGoogleLink } from '../../ctapi/calendarService';

export default function GoogleAuthConfirmHandler() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const googleEmail = searchParams.get('google_email');
    const userEmail = searchParams.get('user_email');
    const token = searchParams.get('token');

    // DEBUG: Log parameters
    useEffect(() => {
        console.log("GoogleAuthConfirmHandler mounted.");
        console.log("URL Parameters:", Object.fromEntries([...searchParams]));
        console.log("Token to verify:", token);
    }, [searchParams, token]);

    const handleConfirm = async () => {
        setIsLoading(true);
        setError('');
        
        console.log("Submitting confirmation with token:", token);

        try {
            const response = await confirmGoogleLink(token);
            console.log("Confirmation API Response:", response);

            navigate('/?oauth_redirect=true&linked=true');

        } catch (err) {
            console.error("Confirmation API Failed:", err);
            if (err.response) {
                console.error("Server Response Data:", await err.response.json());
            }
            
            setError(err.message || 'Something went wrong. Please try again or contact support.');
            setIsLoading(false);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                bgcolor: 'background.default',
                p: 2
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    p: 4,
                    textAlign: 'center',
                    borderRadius: 4,
                    maxWidth: '550px'
                }}
            >
                <Typography variant="h5" sx={{ fontWeight: 500, mb: 2 }}>
                    Confirm Account Link
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 1, mb: 3, textAlign: 'left' }}>
                    You are about to link the Google Account associated with:
                    <Typography component="div" sx={{ fontWeight: 'bold', my: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>{googleEmail}</Typography>
                    to your Candidate Tools account:
                    <Typography component="div" sx={{ fontWeight: 'bold', my: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>{userEmail}</Typography>
                    This will grant Candidate Tools access to your calendar.
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Stack direction="row" spacing={2} justifyContent="center">
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/settings')}
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirm}
                        disabled={isLoading}
                    >
                        {isLoading ? <SpinningCustomLogo size={24} color="inherit" /> : 'Confirm and Link Account'}
                    </Button>
                </Stack>
            </Paper>
        </Box>
    );
}